import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, MessageCircle, Sparkles, Mic, Heart, HandHelping } from "lucide-react";

const CHANNELS = [
  { icon: Sparkles, name: "Start Here", desc: "Welcome + community rhythm" },
  { icon: Users, name: "The Shift", desc: "Awakening & personal transformation" },
  { icon: MessageCircle, name: "Mindset & Consciousness", desc: "The architecture of thought" },
  { icon: Sparkles, name: "Energy & Frequency", desc: "Embodiment practices" },
  { icon: ArrowRight, name: "Timelines & Shifting", desc: "The decisions that become lives" },
  { icon: Sparkles, name: "Spirituality", desc: "Beyond religion" },
  { icon: Heart, name: "Meditation & Inner Work", desc: "Practice, not performance" },
  { icon: MessageCircle, name: "Ask Tay", desc: "Direct questions" },
  { icon: Heart, name: "Wins & Transformations", desc: "The room witnesses" },
  { icon: HandHelping, name: "Community Support", desc: "People encouraging people" },
  { icon: Mic, name: "Live Conversations", desc: "Voice rooms and events" },
];

const DISCORD_INVITE = process.env.REACT_APP_DISCORD_INVITE_URL || "https://discord.gg/rGcCp8hHD";

export default function Community() {
  return (
    <div className="pt-20 sm:pt-24" data-testid="community-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden aurora grain">
        <div className="absolute inset-0 starfield opacity-40 dark:opacity-70 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-6 py-16 sm:py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="eyebrow mb-6 flex items-center gap-2 text-accent">
              <Users className="h-3 w-3" /> The Shift Community
            </div>
            <h1 className="font-serif font-light text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-7xl tracking-tight mb-6 sm:mb-8">
              You were never meant to walk
              <br />
              <span className="italic text-accent">the journey alone.</span>
            </h1>
            <p className="text-base sm:text-xl text-muted-foreground max-w-2xl leading-relaxed mb-8 sm:mb-10">
              Connect with people who are questioning, healing, growing and discovering a
              deeper version of themselves. Real conversations. Real presence. No performance.
            </p>
            <a
              href={DISCORD_INVITE}
              target="_blank"
              rel="noopener noreferrer"
              className="block sm:inline-block"
              data-testid="community-join-btn"
            >
              <Button
                size="lg"
                className="w-full sm:w-auto btn-pill font-mono tracking-[0.24em] uppercase text-xs h-12 px-8 gap-2"
              >
                Join The Shift <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
            <p className="mt-4 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
              Free to enter • Opens Discord
            </p>
          </motion.div>
        </div>
      </section>

      {/* CHANNELS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-16 sm:py-24">
        <div className="mb-10 sm:mb-12">
          <div className="eyebrow mb-3">Inside the room</div>
          <h2 className="font-serif text-3xl sm:text-5xl leading-tight max-w-2xl">
            Eleven rooms for the versions of you that are <span className="italic text-accent">emerging</span>.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border/40">
          {CHANNELS.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.name}
                className="bg-background p-6 sm:p-8 flex items-start gap-4"
                data-testid={`community-channel-${c.name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              >
                <div className="h-10 w-10 rounded-full border border-accent/40 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">
                    Channel
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl leading-snug mb-1">{c.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TIER TEASE */}
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-16 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border/40">
          <div className="bg-background p-8 sm:p-12">
            <div className="eyebrow mb-4">Free</div>
            <h3 className="font-serif text-3xl sm:text-4xl mb-4 leading-snug">
              Community access
            </h3>
            <ul className="space-y-3 text-sm mb-8">
              <li>• All eleven public channels</li>
              <li>• Discussions & basic teachings</li>
              <li>• Weekly community conversations</li>
              <li>• Ask Tay open threads</li>
            </ul>
            <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="block sm:inline-block">
              <Button
                variant="outline"
                className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12"
                data-testid="community-free-btn"
              >
                Enter for free
              </Button>
            </a>
          </div>
          <div className="bg-accent/5 border-l-0 md:border-l border-accent/40 p-8 sm:p-12">
            <div className="eyebrow mb-4 text-accent">Shift+ • $33/mo</div>
            <h3 className="font-serif text-3xl sm:text-4xl mb-4 leading-snug">
              Go deeper.
            </h3>
            <ul className="space-y-3 text-sm mb-8">
              <li>• Exclusive teachings channel</li>
              <li>• Private voice rooms</li>
              <li>• Guided meditation sessions</li>
              <li>• Live Q&A with Tay</li>
              <li>• Exclusive videos & eBooks</li>
              <li>• Members-only events</li>
            </ul>
            <Link to="/membership" className="block sm:inline-block">
              <Button className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 gap-2" data-testid="community-shiftplus-btn">
                Upgrade to Shift+ <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
